import { useState, useEffect, FC, useMemo } from 'react'
import { Card, Button, Separator, Text, Badge, Skeleton, CopyText } from '@/ui'

import FinancialProfileFormModal from './FinancialProfileFormModal'
import {
  viewHelloSignUrls,
  HelloSignResponse,
} from '@/actions/helloSignActions'
import TaxProfileCard from './TaxProfileCard'
import PracticeEditModal from './PracticeEditModal'
import { UserWithAdminInfo } from '@/reducers/admin/allUsersReducer'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { DateTime } from 'luxon'
import {
  adminFetchSmsConsentStatus,
  getSmsConsentStatusByUserId,
  SmsConsentStatusOption,
} from '@/features/user/settings/sections/account-details-card/sms-consent/smsConsentReducer'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  getIsAdministrator,
  selectIsAccountingOpsManager,
  selectIsAccountManager,
} from '@/selectors/user.selectors'
import { TypographyStyles, TextColors } from '@/styles'

interface PracticeInformationCardProps {
  user: UserWithAdminInfo
}

export const PracticeInformationCard: FC<PracticeInformationCardProps> = ({
  user,
}) => {
  const dispatch = useAppDispatch()
  const [isPracticesEditModalOpen, setIsPracticesEditModalOpen] =
    useState(false)
  const [isFinancialAccountModalOpen, setIsFinancialAccountModalOpen] =
    useState(false)
  const [agreements, setAgreements] = useState<HelloSignResponse>()
  const [agreementsLoading, setAgreementsLoading] = useState(true)

  const profile = user.financialProfile

  const smsConsentStatus = useReselector(getSmsConsentStatusByUserId, user.id)

  const isAdministrator = useReselector(getIsAdministrator)
  const isAccountManager = useReselector(selectIsAccountManager)
  const isAccountingOpsManager = useReselector(selectIsAccountingOpsManager)
  const hasEditPermission = useMemo(
    () =>
      Boolean(isAdministrator || isAccountManager || isAccountingOpsManager),
    [isAdministrator, isAccountManager, isAccountingOpsManager]
  )

  useEffect(() => {
    const fetchAgreements = async () => {
      const agreements = await viewHelloSignUrls(user.id)(dispatch)
      setAgreements(agreements)
      setAgreementsLoading(false)
    }

    fetchAgreements()
  }, [dispatch, user.id])

  useEffect(() => {
    if (user.id) {
      dispatch(adminFetchSmsConsentStatus(user.id))
    }
  }, [dispatch, user.id])

  const handleOpenPracticesEditModal = () => {
    setIsPracticesEditModalOpen(true)
  }

  const baaSigned = useMemo(() => {
    const helloSigned = agreements?.baa_url && agreements?.baa_url?.signed
    const tosSigned = user.termsOfService?.baaAcceptedOn
    return helloSigned || tosSigned
  }, [agreements, user])

  const termsOfService = user.termsOfService

  const renderAgreements = () => {
    if (agreementsLoading) {
      return (
        <div className="w-full">
          <Skeleton className="h-6 w-32" />
        </div>
      )
    }

    return (
      <div className="w-full flex flex-col gap-2 my-2">
        <div className="flex items-center gap-2">
          <Text variant={TypographyStyles.BODY_MD} color={TextColors.HEADER}>
            BAA Agreement
          </Text>
          <Badge color={baaSigned ? 'green' : 'red'}>
            {baaSigned ? 'Signed' : 'Unsigned'}
          </Badge>
        </div>

        {termsOfService?.baaAcceptedOn && (
          <Text variant={TypographyStyles.BODY_MD} color={TextColors.HEADER}>
            Signed on{' '}
            {DateTime.fromISO(termsOfService.baaAcceptedOn).toFormat(
              DATE_FORMATS_LUXON.DISPLAY_SHORT
            )}
          </Text>
        )}

        {termsOfService?.baaVersion && (
          <Text variant={TypographyStyles.BODY_MD} color={TextColors.HEADER}>
            BAA Version: {termsOfService.baaVersion}
          </Text>
        )}

        {agreements?.baa_url && agreements?.baa_url?.detailsUrl && (
          <Text variant={TypographyStyles.BODY_MD} color={TextColors.HEADER}>
            HelloSign Link —{' '}
            <a
              href={agreements.baa_url.detailsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              View in HelloSign
            </a>
          </Text>
        )}

        {agreements?.baa_url && agreements?.baa_url?.fileUrl && (
          <Text variant={TypographyStyles.BODY_MD}>
            <a
              href={agreements.baa_url.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Download agreement
            </a>
          </Text>
        )}
      </div>
    )
  }

  if (!profile) {
    return null
  }

  return (
    <>
      <Card className="w-full">
        <div className="flex flex-row justify-between items-end">
          <Text variant={TypographyStyles.H2}>Practice Information</Text>
          <Button
            variant="link"
            onClick={handleOpenPracticesEditModal}
            disabled={!hasEditPermission}
          >
            Edit
          </Button>
        </div>
        <Separator />
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Email
            </Text>
            <CopyText variant={TypographyStyles.BODY_MD} value={user.email} />
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Practice Name
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {profile.businessName}
            </Text>
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Practice Type
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {profile.practiceType}
            </Text>
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Business Address
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {profile.businessAddress}
            </Text>
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              # of Providers
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {profile.practiceType === 'solo'
                ? 1
                : profile.practiceSize
                  ? profile.practiceSize
                  : 0}
            </Text>
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Year Practice Started
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {profile.yearPracticeStarted}
            </Text>
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Self-Described Profession
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {profile.selfDescribedProfession}
            </Text>
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Legal Entity Type
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {profile.businessEntity}
            </Text>
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Business EIN Number
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>{profile.einNumber}</Text>
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Bank Accounts Separate
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {profile.accountsSeparate ? 'Yes' : 'No'}
            </Text>
          </div>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Willing to Separate Accounts
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {profile.accountsSeparate
                ? 'n/a'
                : profile.willingAccountsSeparate
                  ? 'Yes'
                  : 'No'}
            </Text>
          </div>
          {profile.taxEntityType === TAX_ENTITY_TYPES.form_1120_s && (
            <div className="flex items-start gap-x-2">
              <Text
                variant={TypographyStyles.BODY_MD}
                color={TextColors.HEADER}
                className="w-1/3"
              >
                S Corp Effective Date
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                {profile?.entityChangeElectionDate
                  ? DateTime.fromISO(profile?.entityChangeElectionDate)
                      .toUTC()
                      .startOf('day')
                      .toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY)
                  : 'None Added'}
              </Text>
            </div>
          )}
        </div>
        <Separator />
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">{renderAgreements()}</div>
        </div>
        <Separator />

        <div className="flex flex-col gap-2 my-4">
          <div className="flex flex-wrap items-start gap-x-2">
            <Text variant={TypographyStyles.BODY_MD} color={TextColors.HEADER}>
              SMS Mobile Number:
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {smsConsentStatus?.status === SmsConsentStatusOption.OPT_IN
                ? smsConsentStatus?.mobilePhoneNumber
                : ''}
            </Text>
          </div>
          <div className="flex flex-wrap items-start gap-x-2">
            <Text variant={TypographyStyles.BODY_MD} color={TextColors.HEADER}>
              SMS Opt-In Timestamp:
            </Text>
            <Text variant={TypographyStyles.BODY_MD}>
              {smsConsentStatus?.status === SmsConsentStatusOption.OPT_IN
                ? smsConsentStatus?.updatedAt
                : ''}
            </Text>
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <Text variant={TypographyStyles.BODY_LG} color={TextColors.HEADER}>
              Financial Account Info
            </Text>
            <Button
              variant="link"
              onClick={() => setIsFinancialAccountModalOpen(true)}
            >
              Edit
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-x-2">
              <Text
                variant={TypographyStyles.BODY_MD}
                color={TextColors.HEADER}
              >
                Bank Account Read Access:
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                {String(Boolean(profile.bankAccountReadAccess))}
              </Text>
            </div>

            <div className="flex flex-wrap gap-x-2">
              <Text
                variant={TypographyStyles.BODY_MD}
                color={TextColors.HEADER}
              >
                Statement Available Day (latest one):
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                {profile.lastStatementAvailableDay
                  ? profile.lastStatementAvailableDay
                  : 'Not provided'}
              </Text>
            </div>

            <div className="flex flex-wrap gap-x-2">
              <Text
                variant={TypographyStyles.BODY_MD}
                color={TextColors.HEADER}
              >
                Prevent Send Statement Notifications:
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                {String(Boolean(profile.preventMonthlyStatementNotifications))}
              </Text>
            </div>
          </div>
        </div>

        <Separator />
        <div className="flex flex-col gap-2">
          <Text variant={TypographyStyles.BODY_LG} color={TextColors.HEADER}>
            Bookkeeper Info
          </Text>
          <div className="flex items-start gap-x-2">
            <Text
              variant={TypographyStyles.BODY_MD}
              color={TextColors.HEADER}
              className="w-1/3"
            >
              Full Name
            </Text>
            {user.bookkeeper ? (
              <CopyText
                variant={TypographyStyles.BODY_MD}
                value={`${user.bookkeeper.firstName} ${user.bookkeeper.lastName}`}
              />
            ) : (
              <Text variant={TypographyStyles.BODY_MD}>Not assigned</Text>
            )}
          </div>
        </div>

        <FinancialProfileFormModal
          open={isFinancialAccountModalOpen}
          close={() => setIsFinancialAccountModalOpen(false)}
          profile={profile}
        />
        <PracticeEditModal
          user={user}
          open={isPracticesEditModalOpen}
          setOpen={setIsPracticesEditModalOpen}
        />
      </Card>
      <Card>
        <TaxProfileCard userId={user.id} />
      </Card>
    </>
  )
}
